import type {
  AnonymousFixture,
  CanonicalProposal,
  FixtureElement,
  MappingRequest,
  Outcome,
  ProposalEntry
} from "./types";

const geometryAffectingProperties = new Set(["Height", "Width", "Length", "Area", "Volume", "Geometry"]);

function cloneVerbatim<T>(value: T): T {
  return structuredClone(value);
}

export function inspectionOutcome(element: FixtureElement): Outcome {
  if (element.unsupportedReason !== undefined) {
    return "unsupported";
  }
  if (element.classification === null) {
    return "unmapped";
  }
  return "unchanged";
}

function entryFor(element: FixtureElement, request: MappingRequest): ProposalEntry {
  const before = cloneVerbatim(element.properties);
  const common = {
    elementId: element.id,
    before,
    diff: [],
    unknown: cloneVerbatim(element.unknown)
  };

  if (geometryAffectingProperties.has(request.property)) {
    return { ...common, outcome: "refused", reason: "Geometry-affecting properties cannot be changed.", after: before };
  }
  if (element.unsupportedReason !== undefined) {
    return { ...common, outcome: "unsupported", reason: element.unsupportedReason, after: before };
  }
  if (!element.editable) {
    return { ...common, outcome: "refused", reason: "Fixture policy marks this element as read-only.", after: before };
  }

  return {
    ...common,
    outcome: "proposed",
    after: { ...before, [request.property]: request.value },
    diff: [{ path: `/properties/${request.property}`, before: before[request.property], after: request.value }]
  };
}

export function buildCanonicalProposal(
  fixture: AnonymousFixture,
  selectedElementIds: ReadonlySet<string>,
  request: MappingRequest
): CanonicalProposal {
  if (request.property.trim() === "" || request.value.trim() === "") {
    throw new RangeError("A proposal requires a non-empty property and value.");
  }

  const knownIds = new Set(fixture.elements.map((element) => element.id));
  for (const id of selectedElementIds) {
    if (!knownIds.has(id)) {
      throw new RangeError(`Selected element is not in the fixture: ${id}`);
    }
  }

  const entries = fixture.elements
    .filter((element) => selectedElementIds.has(element.id))
    .sort((left, right) => left.id.localeCompare(right.id))
    .map((element) => entryFor(element, request));

  return {
    schemaVersion: "d0-proposal-v1",
    fixtureId: fixture.id,
    request: { property: request.property, value: request.value },
    entries,
    validation: {
      state: "partial",
      reason: "Workbench evaluates local evidence only; no authoritative validation was requested.",
      assessedElementCount: entries.length
    }
  };
}

export function outcomeForElement(
  element: FixtureElement,
  selectedElementIds: ReadonlySet<string>,
  request: MappingRequest
): Outcome {
  if (!selectedElementIds.has(element.id)) return inspectionOutcome(element);
  if (geometryAffectingProperties.has(request.property)) return "refused";
  if (element.unsupportedReason !== undefined) return "unsupported";
  if (!element.editable) return "refused";
  return "proposed";
}
